/**
 * Single post-login redirect (R3). Used by /login and ?tenant= shells only — not landing modal.
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasUsableSession } from '../utils/authSession';

function readDemoTenant(search) {
  return new URLSearchParams(search).get('tenant')?.trim() || '';
}

/**
 * Where to land after login: a saved deep-link (from a 401 bounce) or `/app`.
 * Consumes (clears) the saved path so it is used only once.
 */
export function resolvePostLoginTarget() {
  const saved = sessionStorage.getItem('redirectAfterLogin');
  if (saved) sessionStorage.removeItem('redirectAfterLogin');
  return saved && saved !== '/login' && saved !== '/' ? saved : '/app';
}

/**
 * Auto-skip the login form when the user is ALREADY authenticated and lands on a
 * dedicated login URL (refresh / bookmark on /login or ?tenant=). The post-submit
 * redirect itself is handled imperatively in LoginForm.handleSubmit so the modal
 * (landing page) also works.
 * @param {{ enabled?: boolean }} options
 */
export function usePostLoginRedirect({ enabled = true } = {}) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const didRedirectRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const isDedicatedLoginPage =
      location.pathname === '/login' || Boolean(readDemoTenant(location.search));
    if (!isDedicatedLoginPage) return undefined;

    // Same predicate as PrivateRoute — present, non-expired token only.
    if (!hasUsableSession(token)) {
      didRedirectRef.current = false;
      return undefined;
    }

    if (didRedirectRef.current) return undefined;
    didRedirectRef.current = true;

    navigate(resolvePostLoginTarget(), { replace: true });
    return undefined;
  }, [enabled, token, navigate, location.pathname, location.search]);
}
